import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { CrudItemBase, CrudAction, CrudStats } from "./types/crud";

interface CrudCardProps<T extends CrudItemBase> {
  item: T;
  actions: CrudAction[];
  stats?: CrudStats;
  additionalBadges?: React.ReactNode;
  additionalInfo?: React.ReactNode;
}

export function CrudCard<T extends CrudItemBase>({
  item,
  actions,
  stats,
  additionalBadges,
  additionalInfo,
}: CrudCardProps<T>) {
  const getStatusVariant = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      draft: "outline",
      published: "default",
      archived: "secondary",
    };
    return variants[status] || "default";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              {item.name}
              <Badge variant={getStatusVariant(item.status)}>
                {item.status}
              </Badge>
              {item.version && (
                <Badge variant="outline">v{item.version}</Badge>
              )}
              {item.is_latest_version === false && (
                <Badge variant="secondary">Old Version</Badge>
              )}
              {additionalBadges}
            </CardTitle>
            <CardDescription>{item.description}</CardDescription>
            {stats && (
              <p className="text-xs text-muted-foreground mt-2">
                {stats.total_uses !== undefined && `Used ${stats.total_uses} times`}
                {stats.unique_users !== undefined && ` by ${stats.unique_users} users`}
                {stats.uses_last_7_days !== undefined && ` • 7d: ${stats.uses_last_7_days}`}
              </p>
            )}
            {additionalInfo}
          </div>
          <div className="flex gap-2 shrink-0">
            {actions
              .filter((action) => !action.hidden)
              .map((action, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant={action.variant || "outline"}
                  onClick={action.onClick}
                  title={action.title}
                >
                  <action.icon className="h-4 w-4" />
                  {action.label && (
                    <span className="ml-1">{action.label}</span>
                  )}
                </Button>
              ))}
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
